import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Dropdown } from 'react-bootstrap';
import { Color } from '../../../../core/enums/Color';

import Modal from '../../../components/modal/Modal';
import Button from '../../../../components/form/button/Button';
import { FILE_LOCATION } from '../../../../core/constants/api';
import { Actions as BookingActions } from '../../../../core/state/bookings/actions';
import { BookingModalType } from '../../../../core/state/bookings/types';
import { RootState } from '../../../../core/state/state';
import { isMobileScreenSize } from '../../../../core/utils/isMobileScreenSize';

const LogisticPlanModal: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isMobileScreen = isMobileScreenSize();

  const { isOpen: isAnyModalOpen, data } = useSelector((state: RootState) => state.bookings.modal);
  const project = useSelector((state: RootState) => state.projects.projectDetails);
  const logisticPlans = project?.projectPlanDocuments ?? [];

  const isOpen = isAnyModalOpen && data?.type === BookingModalType.LogisticPlan;

  const openNewBookingModal = useCallback(() => {
    dispatch(
      BookingActions.openModal({
        type: BookingModalType.External,
        initialValues: data?.initialValues
      })
    );
  }, [dispatch, data]);

  const closeModal = useCallback(() => {
    if (isOpen) {
      dispatch(BookingActions.closeModal());
    }
  }, [dispatch, isOpen]);

  useEffect(() => {
    if (isOpen && project && logisticPlans.length === 0) {
      openNewBookingModal();
    }
  }, [isOpen, project, logisticPlans.length, openNewBookingModal]);

  const title = <h3 className="modal-title">{t('booking.openLogisticPlan')}</h3>;
  const buttons = (
    <div className="modal-buttons">
      {logisticPlans.length === 1 ? (
        <Button
          onClick={() => window.open(`${FILE_LOCATION}/${logisticPlans[0].fullName}`, '_blank')}
          buttonTypes={!isMobileScreen ? ['blue-outline'] : ['blue-outline', 'block']}
          color={Color.Blue}
        >
          {t(!isMobileScreen ? 'booking.openLogisticPlan' : 'booking.status.open')}
        </Button>
      ) : (
        <Dropdown alignRight>
          <Dropdown.Toggle
            id="open-logistic-plan"
            variant="transparent"
            childBsPrefix="button"
            className={`button button--color-blue button--blue-outline ${isMobileScreen ? 'button--block' : ''}`}
          >
            {t(!isMobileScreen ? 'booking.openLogisticPlan' : 'booking.status.open')}
          </Dropdown.Toggle>
          <Dropdown.Menu>
            {logisticPlans.map((document) => (
              <Dropdown.Item key={document.id} onClick={() => window.open(`${FILE_LOCATION}/${document.fullName}`, '_blank')}>
                {document.name}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
      )}

      <Button type="submit" buttonTypes={!isMobileScreen ? ['primary'] : ['primary', 'block']} color={Color.Blue} onClick={openNewBookingModal}>
        {t('common.continue')}
      </Button>
    </div>
  );

  return (
    <Modal className={isMobileScreen ? 'modal--small-mobile' : ''} isOpen={isOpen} closeModal={closeModal} title={title} toolbar={buttons}>
      <div className="modal__text">{t('booking.openLogisticPlan.description')}</div>
    </Modal>
  );
};

export default LogisticPlanModal;
